import React, { FunctionComponent, useCallback, useMemo, useState } from 'react';
import Select from 'react-select';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { ConnectedDialog } from '../ConnectedDialog';
import { closeDialog } from '../../../app/dialog/dialog.actions';
import { dialogSelectors } from '../../../app/dialog/dialog.selectors';
import { useSelect, useSelectWithParams } from '../../../utils/selector.utils';
import { nodeSelectors } from '../../../app/resource/node/node.selectors';
import { descendantIdsOfNode } from '../../../app/resource/node/node.utils';
import { FormRadioInput } from '../../pages/common/forms/AnnualForm/RadioInput';
import { INodeDeleteDialogProps } from './types';

const CHILDREN_ACTION_OPTIONS = [
  { id: 'node-delete-leave', value: 'leave', label: 'Leave without parent' },
  { id: 'node-delete-reassign', value: 'reassign', label: 'Reassign to another seat' },
];

interface ISelectOption {
  value: string;
  label: string;
}

interface INodeDeleteForm {
  childrenAction: string;
}

export const NodeDeleteDialog: FunctionComponent = () => {
  const dispatch = useDispatch();
  const props = useSelectWithParams(dialogSelectors.selectDialogProps, 'nodeDeleteDialog') as INodeDeleteDialogProps;

  const allNodes = useSelect(nodeSelectors.selectResourceList);

  const childNodes = useMemo(() => (props ? allNodes.filter((n) => n.parentIds?.includes(String(props.nodeId))) : []), [
    allNodes,
    props,
  ]);
  const childrenNeedingAction = useMemo(() => childNodes.filter((child) => (child.parentIds?.length ?? 0) === 1), [
    childNodes,
  ]);

  const excludedIds = useMemo(() => {
    if (!props) {
      return new Set<string>();
    }
    const self = String(props.nodeId);
    const descendants = descendantIdsOfNode(self, allNodes);
    return new Set<string>([self, ...Array.from(descendants)]);
  }, [props, allNodes]);

  const parentOptions = useMemo<ISelectOption[]>(
    () =>
      allNodes
        .filter((n) => !excludedIds.has(String(n.id)))
        .map((n) => ({ value: String(n.id), label: n.nodeTitle.trim() })),
    [allNodes, excludedIds],
  );

  const formProps = useForm<INodeDeleteForm>({ defaultValues: { childrenAction: 'leave' } });
  const childrenAction = formProps.watch('childrenAction');

  const [selectedParents, setSelectedParents] = useState<ISelectOption[]>([]);

  const onClose = useCallback(() => {
    dispatch(closeDialog('nodeDeleteDialog'));
    formProps.reset();
    setSelectedParents([]);
  }, [dispatch, formProps]);

  const onDelete = useCallback(() => {
    if (!props) {
      return;
    }
    const newParentIds = childrenAction === 'reassign' ? selectedParents.map((o) => o.value) : [];
    props.onConfirm(newParentIds);
    formProps.reset();
    setSelectedParents([]);
  }, [props, childrenAction, selectedParents, formProps]);

  return (
    <ConnectedDialog dialogKey="nodeDeleteDialog">
      <div className="popup delete_popup isVisible">
        <div className="panel">
          <div className="panel-header small-header flex jcsb">
            <h3>Delete seat</h3>
            <span className="close-popup close" onClick={onClose}>
              <i className="icon-close" />
            </span>
          </div>
          <div className="panel-body">
            <h2 className="mb-4 mt-1 text-center">
              Deleting a seat is permanent and cannot be undone. Do you still want to continue?
            </h2>
            {childrenNeedingAction.length > 0 && (
              <div className="mb-4">
                <p className="mb-2">
                  <strong>
                    This seat is the only parent of {childrenNeedingAction.length} child seat
                    {childrenNeedingAction.length > 1 ? 's' : ''}.
                  </strong>{' '}
                  What would you like to do with {childrenNeedingAction.length > 1 ? 'them' : 'it'}?
                </p>
                <FormProvider {...formProps}>
                  <div className="field-container flex justify-start mt-2 themed-radio">
                    <FormRadioInput id="childrenAction" name="childrenAction" options={CHILDREN_ACTION_OPTIONS} />
                  </div>
                </FormProvider>
                {childrenAction === 'reassign' && (
                  <div className="mt-2">
                    <label htmlFor="reassign-parent">New parent seat(s)</label>
                    <Select
                      inputId="reassign-parent"
                      className="custom-select-new"
                      options={parentOptions}
                      value={selectedParents}
                      onChange={(selected) => setSelectedParents(selected as ISelectOption[])}
                      placeholder="Choose parent seat(s)..."
                      isMulti
                      closeMenuOnSelect={false}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex aic jcc" style={{ paddingBottom: '16px' }}>
            <button className="btn btn-border white-bg close mr-1" style={{ cursor: 'pointer' }} onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn red-bg white"
              onClick={onDelete}
              disabled={
                childrenNeedingAction.length > 0 && childrenAction === 'reassign' && selectedParents.length === 0
              }
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </ConnectedDialog>
  );
};
